import { useCallback, useEffect, useRef, useState } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import {
  convertCountryCodeToCountryName,
  PaymentMethod,
  CountryCode,
  FormType,
  FulfillmentMethod,
  isBankDebitPaymentMethod,
} from "@simplyk/common";
import { useElements } from "@stripe/react-stripe-js";
import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from "@stripe/stripe-js";
import { Control, FieldErrors, FieldValues, Path, UseFormSetValue, UseFormTrigger, useWatch } from "react-hook-form";
import { v4 } from "uuid";

import { useMemberOnlyCheckoutEmail } from "@/canvas/features/FormV2/helpers/getMemberOnlyCheckoutEmail";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { ExpressCheckoutWallet } from "@/common/contexts/StripeContext";
import { mapWalletShippingAddressToFields } from "@/common/helpers/fulfillmentCheckout";
import { extractPaths, removeKeys } from "@/common/helpers/utils";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";

export type ExpressCheckoutAnalyticsContext = {
  currentStep: string;
  formType: FormType;
  formObjectId: string;
};

type ExpressCheckoutProps<T extends FieldValues> = {
  handleFormSubmit: (isExpressCheckout: true) => (event?: React.SyntheticEvent) => Promise<boolean>;
  control: Control<T>;
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  trigger: UseFormTrigger<T>;
  formStateErrors: FieldErrors<T>;
  setExpressCheckoutWallet: (wallet: ExpressCheckoutWallet | undefined) => void;
  organizationName: string;
  setIsSubmitting: (value: boolean) => void;
  displayExpressCheckout: boolean;
  handleSetSupportsExpressCheckout: (value: boolean) => void;
  setSelectedPaymentMethod: (method: PaymentMethod | undefined, complete?: boolean) => void;
  selectedPaymentMethod: PaymentMethod | undefined;
  defaultSelectedPaymentMethod: PaymentMethod | undefined;
  isExpressCheckoutLoading: boolean;
  prioritizeBankPayment: boolean;
  analyticsContext?: ExpressCheckoutAnalyticsContext;
  fulfillment?: {
    isEnabled: boolean;
    shippingOnly: boolean;
    shippingRateLabel: string;
  };
};

const BILLING_FIELDS = ["email", "firstName", "lastName", "address", "postalCode", "city", "region", "country"];

const SHIPPING_FIELDS = [
  "shippingFullName",
  "shippingAddress",
  "shippingCity",
  "shippingPostalCode",
  "shippingRegion",
  "shippingCountry",
];

const expressCheckoutRetryMax = 3;
const expressCheckoutRetryDelay = 400;

// Pass the isExpressCheckoutLoading to false after 10 seconds
const expressCheckoutLoadingTimeout = 10000;

export const useExpressCheckout = <T extends FieldValues>({
  handleFormSubmit,
  setValue,
  trigger,
  formStateErrors,
  control,
  setExpressCheckoutWallet,
  organizationName,
  setIsSubmitting,
  displayExpressCheckout,
  handleSetSupportsExpressCheckout,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  defaultSelectedPaymentMethod,
  isExpressCheckoutLoading,
  prioritizeBankPayment,
  analyticsContext,
  fulfillment,
}: ExpressCheckoutProps<T>) => {
  const { logAmplitudeEvent } = useAmplitude();

  const memberOnlyCheckoutEmail = useMemberOnlyCheckoutEmail();

  const elements = useElements();

  const selectedPaymentMethodRef = useRef(selectedPaymentMethod);
  selectedPaymentMethodRef.current = selectedPaymentMethod;

  const step2ArrivalTimeRef = useRef<number | null>(null);
  const hasLoggedStep2WaitRef = useRef(false);

  const currentStep = analyticsContext?.currentStep;
  const formType = analyticsContext?.formType;
  const formObjectId = analyticsContext?.formObjectId;

  useEffect(() => {
    if (currentStep !== "payment") {
      step2ArrivalTimeRef.current = null;
      return;
    }

    if (hasLoggedStep2WaitRef.current) {
      return;
    }

    if (!isExpressCheckoutLoading) {
      hasLoggedStep2WaitRef.current = true;
      datadogLogs.logger.info("Express checkout loading wait time on step 2: 0.00s", {
        elapsedMs: 0,
        ...(formType !== undefined ? { formType } : {}),
        ...(formObjectId !== undefined ? { formId: formObjectId } : {}),
        preloadedBeforeStep2: true,
      });
      return;
    }

    step2ArrivalTimeRef.current = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (
      isExpressCheckoutLoading ||
      !step2ArrivalTimeRef.current ||
      hasLoggedStep2WaitRef.current ||
      currentStep !== "payment"
    ) {
      return;
    }

    const elapsedMs = Date.now() - step2ArrivalTimeRef.current;
    const elapsedSeconds = (elapsedMs / 1000).toFixed(2);
    hasLoggedStep2WaitRef.current = true;
    step2ArrivalTimeRef.current = null;

    datadogLogs.logger.info(`Express checkout loading wait time on step 2: ${elapsedSeconds}s`, {
      elapsedMs,
      ...(formType !== undefined ? { formType } : {}),
      ...(formObjectId !== undefined ? { formId: formObjectId } : {}),
      preloadedBeforeStep2: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpressCheckoutLoading]);

  const isCypressTest = typeof window !== "undefined" && Boolean((window as Window & { Cypress?: unknown }).Cypress);

  const countryField = "country" as Path<T>;
  const country = useWatch({ control, name: countryField });
  const { currentCountryRegions } = useCountryRegion(country);

  const fulfillmentMethodField = "fulfillmentMethod" as Path<T>;
  const fulfillmentMethod = useWatch({ control, name: fulfillmentMethodField });
  const shouldCollectWalletShippingAddress =
    Boolean(fulfillment?.isEnabled) &&
    (fulfillmentMethod === FulfillmentMethod.Shipping || Boolean(fulfillment?.shippingOnly));

  // We use a key to force express checkout to re-render
  const [expressCheckoutKey, setExpressCheckoutKey] = useState(v4());

  // Loader for express checkout when loading/retrying
  const [showExpressCheckoutLoader, setShowExpressCheckoutLoader] = useState(true);
  const [availableWallets, setAvailableWallets] = useState({ applePay: false, googlePay: false });

  useEffect(() => {
    if ((isExpressCheckoutLoading || showExpressCheckoutLoader) && !isCypressTest) {
      const timeout = setTimeout(() => {
        handleSetSupportsExpressCheckout(false);
        setShowExpressCheckoutLoader(false);
      }, expressCheckoutLoadingTimeout);

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpressCheckoutLoading, showExpressCheckoutLoader]);

  // We use a ref to keep track of the number of retries
  const retryCount = useRef(0);
  const hasReportedWallet = useRef(false);

  // We use a ref to keep track of the timeout
  // To have only one timeout to cycle the express checkout key at a time
  const timeout = useRef<NodeJS.Timeout>(undefined);

  // Cycle the express checkout key to force the express checkout button to re-render
  const cycleExpressCheckoutKey = useCallback((timeoutDelay: number = expressCheckoutRetryDelay) => {
    // Clear the timeout if it exists
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    // Set a new timeout to cycle the express checkout key
    timeout.current = setTimeout(() => {
      setExpressCheckoutKey(v4());
    }, timeoutDelay);
  }, []);

  const resetExpressCheckoutRetries = useCallback(() => {
    retryCount.current = 0;
  }, []);

  // We skip the first cycle to avoid the express checkout key being cycled on the first render
  const isFirstCycle = useRef(true);
  useEffect(() => {
    if (displayExpressCheckout) {
      if (!isFirstCycle.current) {
        cycleExpressCheckoutKey();
      }
      isFirstCycle.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayExpressCheckout]);

  // Retry logic for express checkout if it fails to load
  // We retry up to the set limit, increasing the delay by 100ms each time.
  const retryLoadingExpressCheckout = useCallback(() => {
    setShowExpressCheckoutLoader(true);
    retryCount.current++;
    cycleExpressCheckoutKey(expressCheckoutRetryDelay + retryCount.current * 100);
  }, [cycleExpressCheckoutKey]);

  const handleExpressCheckoutReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      setShowExpressCheckoutLoader(false);
      setAvailableWallets({
        applePay: Boolean(event.availablePaymentMethods?.applePay),
        googlePay: Boolean(event.availablePaymentMethods?.googlePay),
      });
      if (event.availablePaymentMethods && containsTrueValue(event.availablePaymentMethods)) {
        // Reset the retry count if express checkout is available
        resetExpressCheckoutRetries();
        hasReportedWallet.current = true;
        handleSetSupportsExpressCheckout(true);

        if (!prioritizeBankPayment && !isBankDebitPaymentMethod(selectedPaymentMethodRef.current)) {
          //set payment method to card in preview mode even if expressCheckout is available
          setSelectedPaymentMethod(defaultSelectedPaymentMethod);
          elements?.getElement("payment")?.collapse();
        }
        return;
      }

      if (hasReportedWallet.current && retryCount.current < expressCheckoutRetryMax) {
        retryLoadingExpressCheckout();
        return;
      }

      // If we reach the retry limit, we stop retrying and hide the express checkout section.
      handleSetSupportsExpressCheckout(false);
    },
    [
      handleSetSupportsExpressCheckout,
      resetExpressCheckoutRetries,
      setSelectedPaymentMethod,
      defaultSelectedPaymentMethod,
      elements,
      retryLoadingExpressCheckout,
      prioritizeBankPayment,
    ]
  );

  const handleExpressCheckoutClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      const bypassedFields = shouldCollectWalletShippingAddress
        ? [...BILLING_FIELDS, ...SHIPPING_FIELDS]
        : BILLING_FIELDS;
      const errorsWithoutBillingInfo = removeKeys(formStateErrors ?? {}, bypassedFields);
      if (Object.keys(errorsWithoutBillingInfo).length > 0) {
        if (event.expressPaymentType === "apple_pay") {
          const keys = extractPaths(errorsWithoutBillingInfo);
          const errorElements = keys.map((name) => document.getElementsByName(name)[0]).filter((el) => !!el);
          errorElements.sort((a, b) => b.scrollHeight - a.scrollHeight);
          errorElements[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElements[0]?.focus({ preventScroll: true });
        }
        void trigger(undefined, { shouldFocus: true });
        return;
      }

      event.resolve({
        emailRequired: true,
        billingAddressRequired: true,
        business: { name: organizationName },
        ...(shouldCollectWalletShippingAddress
          ? {
              shippingAddressRequired: true,
              shippingRates: [
                {
                  id: "fulfillment-shipping",
                  displayName: fulfillment?.shippingRateLabel ?? "Shipping",
                  amount: 0,
                },
              ],
            }
          : {}),
      });

      setSelectedPaymentMethod(PaymentMethod.ApplePayOrGooglePay);
      setExpressCheckoutWallet(event.expressPaymentType);
    },
    [
      formStateErrors,
      organizationName,
      setExpressCheckoutWallet,
      setSelectedPaymentMethod,
      trigger,
      shouldCollectWalletShippingAddress,
      fulfillment?.shippingRateLabel,
    ]
  );

  const handleExpressCheckoutConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      setIsSubmitting(true);
      const parts = event.billingDetails?.name?.trim().split(" ");
      const details = event.billingDetails;
      const firstName = parts?.[0] || details?.name || "N A";
      const lastName = parts?.slice(1).join(" ") || "N A";
      setValue("email", memberOnlyCheckoutEmail ?? details?.email);
      setValue("firstName", firstName);
      setValue("lastName", lastName);
      setValue("address", details?.address?.line1);
      setValue("postalCode", details?.address?.postal_code);
      setValue("city", details?.address?.city);
      const region = currentCountryRegions.find((region) => region.shortCode === details?.address.state)?.shortCode;
      setValue("region", region || details?.address.state);
      setValue("country", convertCountryCodeToCountryName(details?.address?.country as CountryCode));

      if (shouldCollectWalletShippingAddress && event.shippingAddress) {
        const shippingFields = mapWalletShippingAddressToFields(event.shippingAddress);
        const setShippingValue = setValue as unknown as UseFormSetValue<TicketingPaymentInput>;
        setShippingValue("shippingSameAsBilling", false);
        setShippingValue("shippingFullName", shippingFields.shippingFullName ?? "");
        setShippingValue("shippingAddress", shippingFields.shippingAddress ?? "");
        setShippingValue("shippingCity", shippingFields.shippingCity ?? "");
        setShippingValue("shippingPostalCode", shippingFields.shippingPostalCode ?? "");
        setShippingValue("shippingRegion", shippingFields.shippingRegion ?? "");
        setShippingValue("shippingCountry", shippingFields.shippingCountry ?? "");
      }

      let paymentSucceeded = false;
      try {
        paymentSucceeded = await handleFormSubmit(true)();
      } finally {
        if (!paymentSucceeded) {
          event.paymentFailed({ reason: "fail" });
        }
      }
    },
    [
      currentCountryRegions,
      handleFormSubmit,
      memberOnlyCheckoutEmail,
      setIsSubmitting,
      setValue,
      shouldCollectWalletShippingAddress,
    ]
  );

  const handleExpressCheckoutShippingAddressChange = useCallback(
    (event: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
      event.resolve();
    },
    []
  );

  const handleExpressCheckoutShippingRateChange = useCallback(
    (event: StripeExpressCheckoutElementShippingRateChangeEvent) => {
      event.resolve();
    },
    []
  );

  const handleExpressCheckoutLoadError = useCallback(() => {
    setAvailableWallets({ applePay: false, googlePay: false });
    // Check if this triggers when not loaded to unhide.
    handleSetSupportsExpressCheckout(false);
    logAmplitudeEvent(AmplitudeEvents.PaymentExpressCheckoutLoadedFailed);
    setShowExpressCheckoutLoader(false);
    setExpressCheckoutWallet(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setExpressCheckoutWallet, handleSetSupportsExpressCheckout]);

  const handleExpressCheckoutCancel = useCallback(() => {
    setExpressCheckoutWallet(undefined);
    setSelectedPaymentMethod(defaultSelectedPaymentMethod);
  }, [defaultSelectedPaymentMethod, setExpressCheckoutWallet, setSelectedPaymentMethod]);

  return {
    availableWallets,
    expressCheckoutKey,
    showExpressCheckoutLoader,
    handleExpressCheckoutReady,
    handleExpressCheckoutClick,
    handleExpressCheckoutConfirm,
    handleExpressCheckoutLoadError,
    handleExpressCheckoutCancel,
    handleExpressCheckoutShippingAddressChange,
    handleExpressCheckoutShippingRateChange,
  };
};

const containsTrueValue = (obj: Record<string, boolean>): boolean => {
  for (const key in obj) {
    if (obj[key]) {
      return true;
    }
  }
  return false;
};
